import axios from "axios";
import React, { useEffect, useState } from "react";
import {
  FiCreditCard,
  FiDollarSign,
  FiClock,
  FiArrowUp,
  FiArrowDown,
  FiRefreshCw,
  FiX,
  FiPlus,
  FiMinus,
  FiInfo,
  FiClipboard,
  FiCheckCircle,
  FiZap,
  FiSearch,
  FiTrendingUp,
  FiShield,
  FiArrowUpRight,
  FiArrowDownLeft,
} from "react-icons/fi";
import { FaRupeeSign } from "react-icons/fa";
import CountUp from "react-countup";
import { toast } from "react-toastify";
import "../index.css";
import "./EmployeeDashboard.css";
import "./EmployeeLeaves.css";

const API_BASE_URL = "https://api.ingrainsystems.com/api";

const PRESET_AMOUNTS = [100, 250, 500, 1000, 2000, 5000];


const MyWallet = () => {
  const [walletData, setWalletData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [showRechargeModal, setShowRechargeModal] = useState(false);
  const [rechargeCoins, setRechargeCoins] = useState("");
  const [rechargeLoading, setRechargeLoading] = useState(false);
  const [rechargeMessage, setRechargeMessage] = useState({ text: "", type: "" });

  const getClientId = () => {
    return localStorage.getItem("clientId") || "";
  };

  useEffect(() => {
    fetchWalletData();
    // Load Razorpay script
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const fetchWalletData = async () => {
    try {
      setLoading(true);
      setError(null);
      const clientId = getClientId();

      if (!clientId) {
        setError("Client ID not found. Please login again.");
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/clients/my-wallet/${clientId}`);

      if (response.data.success) {
        setWalletData(response.data.data);
      } else {
        setError(response.data.message || "Failed to fetch wallet data");
      }
    } catch (err) {
      console.error("Error fetching wallet:", err);
      setError(err.response?.data?.message || "Failed to fetch wallet data");
    } finally {
      setLoading(false);
    }
  };

  const handleRecharge = async (customCoins) => {
    const coinsToUse = customCoins !== undefined ? customCoins : parseInt(rechargeCoins, 10);

    if (!coinsToUse || coinsToUse < 1) {
      const msg = "Please enter a valid number of coins (minimum 1)";
      setRechargeMessage({ text: msg, type: "error" });
      toast.error(msg);
      return;
    }

    try {
      setRechargeLoading(true);
      setRechargeMessage({ text: "", type: "" });

      const clientId = getClientId();

      // Create Razorpay order
      const orderResponse = await axios.post(
        `${API_BASE_URL}/clients/recharge-wallet/${clientId}`,
        { coins: coinsToUse }
      );

      if (orderResponse.data.success) {
        const options = {
          key: "rzp_test_BxtRNvflG06PTV", // Razorpay Key ID
          amount: orderResponse.data.data.amount,
          currency: orderResponse.data.data.currency,
          name: "Timely Health Care",
          description: `Recharge Wallet with ${coinsToUse} Coins`,
          order_id: orderResponse.data.data.orderId,
          handler: async (response) => {
            // Verify payment
            try {
              const verifyResponse = await axios.post(
                `${API_BASE_URL}/clients/verify-wallet-recharge/${clientId}`,
                {
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_signature: response.razorpay_signature,
                }
              );

              if (verifyResponse.data.success) {
                const successMsg = `Successfully added ${coinsToUse} coins to your wallet!`;
                setRechargeMessage({ text: successMsg, type: "success" });
                toast.success(successMsg);
                setTimeout(() => {
                  setShowRechargeModal(false);
                  setRechargeCoins("");
                  setRechargeMessage({ text: "", type: "" });
                  fetchWalletData(); // Refresh wallet data
                }, 1500);
              } else {
                const errText = verifyResponse.data.message || "Payment verification failed";
                setRechargeMessage({ text: errText, type: "error" });
                toast.error(errText);
              }
            } catch (err) {
              console.error("Verification error:", err);
              const errText = err.response?.data?.message || "Payment verification failed";
              setRechargeMessage({ text: errText, type: "error" });
              toast.error(errText);
            }
          },
          prefill: {
            email: localStorage.getItem("clientEmail") || "",
            name: localStorage.getItem("clientName") || "",
          },
          notes: {
            clientId: clientId,
            coins: coinsToUse,
          },
          theme: {
            color: "#4f46e5",
          },
          modal: {
            ondismiss: () => {
              setRechargeLoading(false);
              setRechargeMessage({ text: "Payment cancelled", type: "error" });
            },
          },
        };

        const razorpayInstance = new window.Razorpay(options);
        razorpayInstance.open();
      } else {
        const failText = orderResponse.data.message || "Failed to create payment order";
        setRechargeMessage({ text: failText, type: "error" });
        toast.error(failText);
      }
    } catch (err) {
      console.error("Recharge error:", err);
      const failText = err.response?.data?.message || "Failed to process recharge order";
      setRechargeMessage({ text: failText, type: "error" });
      toast.error(failText);
    } finally {
      setRechargeLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const transactionHistory = walletData?.history || [];
  const sortedHistory = [...transactionHistory].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
  );

  const filteredTransactions = sortedHistory.filter((transaction) => {
    const matchesFilter =
      selectedFilter === "all" ? true : transaction.type === selectedFilter;
    const message = (transaction.message || "").toLowerCase();
    const matchesSearch = searchQuery.trim()
      ? message.includes(searchQuery.toLowerCase())
      : true;
    return matchesFilter && matchesSearch;
  });

  const totalCredited = sortedHistory
    .filter((t) => t.type === "credit")
    .reduce((sum, t) => sum + (Number(t.coins) || 0), 0);

  const totalDebited = sortedHistory
    .filter((t) => t.type === "debit")
    .reduce((sum, t) => sum + (Number(t.coins) || 0), 0);

  const totalCoins = Number(walletData?.coins ?? walletData?.totalCoins ?? 0);

  if (loading) {
    return (
      <div style={{ backgroundColor: "white" }} className="emp-dash min-h-screen">
        <main className="p-4 sm:p-6 lg:p-8">
          <div className="py-24 text-center">
            <div className="flex flex-col items-center justify-center gap-3">
              <div className="w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
              <span className="text-xs font-bold text-gray-500 uppercase tracking-widest">
                Syncing wallet data...
              </span>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ backgroundColor: "white" }} className="emp-dash min-h-screen">
        <main className="p-4 sm:p-6 lg:p-8">
          <div className="max-w-md mx-auto my-12 p-6 bg-rose-50 border border-rose-200 rounded-2xl text-center space-y-4">
            <div className="w-12 h-12 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center mx-auto">
              <FiX size={24} />
            </div>
            <div>
              <h3 className="text-base font-bold text-rose-900">Wallet Error</h3>
              <p className="text-xs text-rose-700 mt-1">{error}</p>
            </div>
            <button
              onClick={fetchWalletData}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold uppercase tracking-widest rounded-xl transition-all shadow-md shadow-indigo-600/20"
            >
              <FiRefreshCw size={14} /> Try Again
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: "white" }} className="emp-dash">
      <main>
        {/* Page Header */}
        <div className="emp-dash__header">
          <div>
            <h1 className="emp-dash__greeting">
              My <span>Wallet</span>
            </h1>
            <p className="emp-dash__subtitle">
              Manage your balance, add coins, and monitor your recruitment expenditures.
            </p>
          </div>
          <div className="emp-dash__date-pill">
            <FiCreditCard />
            <span>
              {new Date().toLocaleDateString("en-US", {
                weekday: "short",
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </span>
          </div>
        </div>

        {/* Global Alert / Status Message */}
        {rechargeMessage.text && (
          <div
            className={`mb-6 p-4 rounded-xl flex items-center justify-between gap-3 ${
              rechargeMessage.type === "success"
                ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                : "bg-rose-50 text-rose-600 border border-rose-100"
            }`}
          >
            <div className="flex items-center gap-3">
              {rechargeMessage.type === "success" ? (
                <FiCheckCircle size={18} className="flex-shrink-0" />
              ) : (
                <FiX size={18} className="flex-shrink-0" />
              )}
              <span className="text-xs font-bold uppercase tracking-wide">
                {rechargeMessage.text}
              </span>
            </div>
            <button
              onClick={() => setRechargeMessage({ text: "", type: "" })}
              className="text-gray-400 hover:text-gray-600"
            >
              <FiX size={16} />
            </button>
          </div>
        )}

        {/* Main Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column (Span 2) */}
          <div className="lg:col-span-2 space-y-6">
            {/* Balance Hero Card */}
            <div className="emp-dash__card p-6 relative overflow-hidden bg-gradient-to-br from-white via-indigo-50/20 to-violet-50/40">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
                    <FiCreditCard size={15} className="text-indigo-600" />
                  </div>
                  <h2 className="text-sm font-bold text-gray-800 uppercase tracking-widest">
                    Wallet Overview
                  </h2>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={fetchWalletData}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-50 text-gray-600 hover:bg-indigo-50 hover:text-indigo-600 text-[10px] font-bold uppercase tracking-widest border border-gray-200 transition-all cursor-pointer"
                    title="Refresh Balance"
                  >
                    <FiRefreshCw size={11} className={loading ? "animate-spin" : ""} />
                    Sync
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
                {/* Balance Big Block */}
                <div className="md:col-span-7 p-6 rounded-2xl bg-gradient-to-r from-indigo-600 via-indigo-700 to-indigo-800 text-white shadow-xl shadow-indigo-600/20 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-36 h-36 bg-white/10 rounded-full blur-2xl -mr-12 -mt-12 pointer-events-none" />
                  <div className="relative">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] font-bold text-indigo-200 uppercase tracking-[0.2em]">
                        Available Balance
                      </span>
                      <span className="inline-flex items-center gap-1 text-[9px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200 border border-emerald-400/30 uppercase tracking-wider">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                        Active
                      </span>
                    </div>

                    <div className="flex items-baseline gap-2 my-2">
                      <FaRupeeSign className="text-indigo-200 text-xl font-bold opacity-80" />
                      <span className="text-3xl sm:text-4xl font-extrabold tracking-tight">
                        <CountUp end={totalCoins} duration={1.5} separator="," />
                      </span>
                      <span className="text-sm font-bold text-indigo-200 uppercase tracking-wider ml-1">
                        Coins
                      </span>
                    </div>

                    <div className="mt-4 pt-3 border-t border-indigo-500/40 flex items-center justify-between text-[11px] text-indigo-100">
                      <span>Rate: 1 Coin = ₹1.00</span>
                      <span className="font-semibold text-white">Instant Credit</span>
                    </div>
                  </div>
                </div>

                {/* Quick Stats Block */}
                <div className="md:col-span-5 grid grid-cols-2 gap-3">
                  <div className="p-3.5 rounded-xl bg-emerald-50/60 border border-emerald-100 flex flex-col justify-between">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[9px] font-bold text-emerald-700 uppercase tracking-widest">
                        Total Credited
                      </span>
                      <div className="w-6 h-6 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center">
                        <FiArrowUpRight size={13} />
                      </div>
                    </div>
                    <p className="text-base font-extrabold text-emerald-700 mt-1">
                      +<CountUp end={totalCredited} duration={1.5} separator="," />
                    </p>
                    <span className="text-[9px] text-emerald-600/80 font-medium mt-0.5">
                      coins added
                    </span>
                  </div>

                  <div className="p-3.5 rounded-xl bg-rose-50/60 border border-rose-100 flex flex-col justify-between">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[9px] font-bold text-rose-700 uppercase tracking-widest">
                        Total Spent
                      </span>
                      <div className="w-6 h-6 rounded-lg bg-rose-100 text-rose-600 flex items-center justify-center">
                        <FiArrowDownLeft size={13} />
                      </div>
                    </div>
                    <p className="text-base font-extrabold text-rose-700 mt-1">
                      -<CountUp end={totalDebited} duration={1.5} separator="," />
                    </p>
                    <span className="text-[9px] text-rose-600/80 font-medium mt-0.5">
                      coins utilized
                    </span>
                  </div>

                  <div className="col-span-2 p-3 rounded-xl bg-gray-50 border border-gray-200/80 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
                        <FiZap size={12} />
                      </div>
                      <span className="text-xs font-bold text-gray-700">Quick Recharge</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowRechargeModal(true)}
                      className="px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-[10px] font-bold uppercase tracking-wider transition-all shadow-sm shadow-indigo-600/20"
                    >
                      + Add Coins
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Transaction History Card */}
            <div className="emp-dash__card p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                    <FiRefreshCw size={15} className="text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-gray-800 uppercase tracking-widest">
                      Transaction History
                    </h2>
                    <p className="text-[10px] text-gray-500">
                      Showing {filteredTransactions.length} records (Latest first)
                    </p>
                  </div>
                </div>

                {/* Filter Buttons */}
                <div className="flex items-center gap-1.5 bg-gray-50 p-1 rounded-xl border border-gray-200">
                  <button
                    type="button"
                    onClick={() => setSelectedFilter("all")}
                    className={`px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all ${
                      selectedFilter === "all"
                        ? "bg-indigo-600 text-white shadow-sm"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    All
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedFilter("credit")}
                    className={`px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 transition-all ${
                      selectedFilter === "credit"
                        ? "bg-emerald-600 text-white shadow-sm"
                        : "text-emerald-700 hover:bg-emerald-50"
                    }`}
                  >
                    <FiArrowUp size={11} /> Credits
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedFilter("debit")}
                    className={`px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 transition-all ${
                      selectedFilter === "debit"
                        ? "bg-rose-600 text-white shadow-sm"
                        : "text-rose-700 hover:bg-rose-50"
                    }`}
                  >
                    <FiArrowDown size={11} /> Debits
                  </button>
                </div>
              </div>

              {/* Search Bar */}
              <div className="relative mb-5">
                <FiSearch
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-indigo-600 transition-colors z-10"
                  size={14}
                />
                <input
                  type="text"
                  placeholder="Search transactions by title or description..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-11 pr-4 py-2.5 bg-gray-50/50 rounded-xl border border-gray-200 focus:bg-white focus:ring-4 focus:ring-indigo-600/5 focus:border-indigo-600 transition-all outline-none text-xs text-gray-800"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <FiX size={14} />
                  </button>
                )}
              </div>

              {/* Transactions List */}
              {filteredTransactions.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed border-gray-100 rounded-2xl">
                  <div className="w-12 h-12 rounded-xl bg-gray-50 flex items-center justify-center mx-auto mb-2 text-gray-300">
                    <FiClipboard size={22} />
                  </div>
                  <p className="text-xs font-bold text-gray-600 uppercase tracking-wider">
                    No transactions found
                  </p>
                  <p className="text-[10px] text-gray-400 mt-1">
                    {searchQuery
                      ? "Try adjusting your search query."
                      : "Transactions will appear here once you recharge or spend coins."}
                  </p>
                </div>
              ) : (
                <div className="space-y-2.5 max-h-[480px] overflow-y-auto pr-1 no-scrollbar">
                  {filteredTransactions.map((tx, idx) => {
                    const isCredit = tx.type === "credit";
                    return (
                      <div
                        key={tx._id || idx}
                        className={`flex items-center justify-between p-3.5 rounded-xl border transition-all ${
                          isCredit
                            ? "bg-emerald-50/30 border-emerald-100/80 hover:bg-emerald-50/60"
                            : "bg-rose-50/30 border-rose-100/80 hover:bg-rose-50/60"
                        }`}
                      >
                        <div className="flex items-center gap-3.5 min-w-0 flex-1">
                          <div
                            className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm ${
                              isCredit
                                ? "bg-emerald-600 text-white"
                                : "bg-rose-600 text-white"
                            }`}
                          >
                            {isCredit ? <FiPlus size={16} /> : <FiMinus size={16} />}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-bold text-gray-800 truncate">
                              {tx.message || (isCredit ? "Wallet Recharge" : "Coins Deducted")}
                            </p>
                            <div className="flex items-center gap-2 mt-1 text-[10px] text-gray-500">
                              <span className="flex items-center gap-1 font-medium">
                                <FiClock size={10} className="text-gray-400" />
                                {formatDateTime(tx.createdAt)}
                              </span>
                              <span className="text-gray-300">•</span>
                              <span className="text-gray-400 font-medium">
                                {formatTimeAgo(tx.createdAt)}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="text-right pl-3 flex-shrink-0">
                          <p
                            className={`text-sm font-extrabold tracking-tight ${
                              isCredit ? "text-emerald-700" : "text-rose-700"
                            }`}
                          >
                            {isCredit ? "+" : "-"}
                            {Number(tx.coins || 0).toLocaleString()}
                          </p>
                          <span className="text-[9px] font-bold uppercase tracking-wider text-gray-400">
                            Coins
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Summary Bottom Strip */}
              {filteredTransactions.length > 0 && (
                <div className="mt-5 pt-4 border-t border-gray-100 grid grid-cols-2 gap-3">
                  <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-100 flex items-center justify-between">
                    <div>
                      <p className="text-[9px] font-bold text-emerald-600 uppercase tracking-widest">
                        Filtered Credits
                      </p>
                      <p className="text-sm font-extrabold text-emerald-800 mt-0.5">
                        +
                        {filteredTransactions
                          .filter((t) => t.type === "credit")
                          .reduce((sum, t) => sum + (Number(t.coins) || 0), 0)
                          .toLocaleString()}{" "}
                        <span className="text-[10px] font-bold">coins</span>
                      </p>
                    </div>
                    <FiArrowUp size={16} className="text-emerald-600" />
                  </div>

                  <div className="p-3 bg-rose-50 rounded-xl border border-rose-100 flex items-center justify-between">
                    <div>
                      <p className="text-[9px] font-bold text-rose-600 uppercase tracking-widest">
                        Filtered Debits
                      </p>
                      <p className="text-sm font-extrabold text-rose-800 mt-0.5">
                        -
                        {filteredTransactions
                          .filter((t) => t.type === "debit")
                          .reduce((sum, t) => sum + (Number(t.coins) || 0), 0)
                          .toLocaleString()}{" "}
                        <span className="text-[10px] font-bold">coins</span>
                      </p>
                    </div>
                    <FiArrowDown size={16} className="text-rose-600" />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Column (Span 1) */}
          <div className="space-y-6">
            {/* Quick Recharge Form Card */}
            <div className="emp-dash__card p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                  <FiZap size={15} className="text-emerald-600" />
                </div>
                <div>
                  <h2 className="text-sm font-bold text-gray-800 uppercase tracking-widest">
                    Quick Recharge
                  </h2>
                </div>
              </div>
              <p className="text-[10px] text-gray-500 mb-4 ml-10">
                1 Coin = ₹1 · Instant credit to account
              </p>

              {/* Amount Presets */}
              <div className="space-y-1.5 mb-4">
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-widest">
                  Select Coins Preset
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {PRESET_AMOUNTS.map((amt) => (
                    <button
                      key={amt}
                      type="button"
                      onClick={() => setRechargeCoins(String(amt))}
                      className={`py-2 px-2 rounded-xl text-xs font-bold border transition-all ${
                        Number(rechargeCoins) === amt
                          ? "bg-indigo-600 text-white border-indigo-600 shadow-md shadow-indigo-600/20 scale-[1.02]"
                          : "bg-gray-50 hover:bg-indigo-50/60 text-gray-700 border-gray-200 hover:border-indigo-200"
                      }`}
                    >
                      +{amt}
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Input */}
              <div className="space-y-1.5 mb-4">
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-widest">
                  Or Enter Custom Amount <span className="text-rose-500">*</span>
                </label>
                <div className="relative group">
                  <FaRupeeSign
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-indigo-600 transition-colors"
                    size={14}
                  />
                  <input
                    type="number"
                    min="1"
                    placeholder="e.g. 500"
                    value={rechargeCoins}
                    onChange={(e) => setRechargeCoins(e.target.value)}
                    className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 focus:ring-4 focus:ring-indigo-600/5 focus:border-indigo-600 transition-all outline-none text-sm font-bold text-gray-800"
                  />
                </div>
              </div>

              {/* Order Summary Box */}
              <div className="p-3.5 bg-gray-50 rounded-xl border border-gray-200/80 mb-4 space-y-2">
                <div className="flex items-center justify-between text-xs text-gray-600">
                  <span>Coins to Add:</span>
                  <span className="font-bold text-gray-900">
                    {rechargeCoins ? Number(rechargeCoins).toLocaleString() : "0"} Coins
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs text-gray-600">
                  <span>Price per Coin:</span>
                  <span className="font-medium text-gray-900">₹1.00</span>
                </div>
                <div className="pt-2 border-t border-gray-200 flex items-center justify-between text-sm">
                  <span className="font-bold text-gray-800">Total Payable:</span>
                  <span className="font-extrabold text-indigo-600">
                    ₹{rechargeCoins ? Number(rechargeCoins).toLocaleString() : "0"}
                  </span>
                </div>
              </div>

              {/* Action Button */}
              <button
                type="button"
                onClick={() => handleRecharge()}
                disabled={rechargeLoading || !rechargeCoins || Number(rechargeCoins) <= 0}
                className="w-full flex items-center justify-center gap-2 py-3.5 px-6 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-indigo-600/20"
              >
                {rechargeLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Processing Order...
                  </>
                ) : (
                  <>
                    <FiCreditCard size={16} />
                    Proceed to Recharge
                  </>
                )}
              </button>

              {/* Trust Badge */}
              <div className="mt-3 flex items-center justify-center gap-1.5 text-[10px] text-gray-500 font-medium">
                <FiShield className="text-emerald-600" size={12} />
                <span>Secured 256-bit Razorpay Gateway</span>
              </div>
            </div>

            {/* Wallet Info & Benefits Card */}
            <div className="emp-dash__card p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center">
                  <FiInfo size={15} className="text-violet-600" />
                </div>
                <h2 className="text-sm font-bold text-gray-800 uppercase tracking-widest">
                  Coin Benefits
                </h2>
              </div>

              <div className="space-y-3">
                <div className="p-3 bg-violet-50/50 rounded-xl border border-violet-100 flex items-start gap-2.5">
                  <FiCheckCircle size={14} className="text-violet-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-bold text-violet-900">Zero Expiry</p>
                    <p className="text-[10px] text-violet-700 mt-0.5">
                      Your purchased coins stay in your wallet indefinitely.
                    </p>
                  </div>
                </div>

                <div className="p-3 bg-indigo-50/50 rounded-xl border border-indigo-100 flex items-start gap-2.5">
                  <FiZap size={14} className="text-indigo-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-bold text-indigo-900">Seamless Hiring</p>
                    <p className="text-[10px] text-indigo-700 mt-0.5">
                      Use coins to post jobs, feature listings, and unlock candidate assessments.
                    </p>
                  </div>
                </div>

                <div className="p-3 bg-amber-50 rounded-xl border border-amber-100 flex items-start gap-2.5">
                  <FiTrendingUp size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-bold text-amber-900">Instant Verification</p>
                    <p className="text-[10px] text-amber-800 mt-0.5">
                      Recharges reflect instantly in your dashboard upon payment completion.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Tip Card matching PostNewJob */}
            <div className="p-4 bg-amber-50 rounded-2xl border border-amber-100 flex items-start gap-3">
              <FiZap size={15} className="text-amber-500 mt-0.5 flex-shrink-0" />
              <p className="text-[11px] text-amber-900 font-medium leading-relaxed">
                Need extra coins for team hiring? Check out our{" "}
                <a
                  href="/specialoffers"
                  className="font-bold text-indigo-600 hover:underline"
                >
                  Special Offers
                </a>{" "}
                and{" "}
                <a href="/myreffral" className="font-bold text-indigo-600 hover:underline">
                  Referral Rewards
                </a>{" "}
                for exclusive bonuses.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-center gap-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest py-8">
          <FiClipboard className="text-indigo-500" />
          Last Updated: {formatDateTime(new Date().toISOString())}
        </div>
      </main>

      {/* Recharge Modal */}
      {showRechargeModal && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
          <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden relative border border-gray-100 animate-in fade-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="px-6 py-5 bg-gradient-to-r from-indigo-50 via-white to-violet-50 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center shadow-md shadow-indigo-600/20">
                  <FiCreditCard size={18} />
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-900">Recharge Wallet</h2>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-indigo-600">
                    Add Coins Instantly
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowRechargeModal(false);
                  setRechargeMessage({ text: "", type: "" });
                }}
                className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-500 flex items-center justify-center transition-colors"
              >
                <FiX size={16} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              <div className="space-y-1.5">
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-widest">
                  Select Quick Coins
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {PRESET_AMOUNTS.map((amt) => (
                    <button
                      key={amt}
                      type="button"
                      onClick={() => setRechargeCoins(String(amt))}
                      className={`py-2 px-2 rounded-xl text-xs font-bold border transition-all ${
                        Number(rechargeCoins) === amt
                          ? "bg-indigo-600 text-white border-indigo-600 shadow-sm"
                          : "bg-gray-50 hover:bg-indigo-50 text-gray-700 border-gray-200"
                      }`}
                    >
                      +{amt}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-widest">
                  Enter Coins (1 Coin = ₹1)
                </label>
                <div className="relative group">
                  <FaRupeeSign
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-indigo-600 transition-colors"
                    size={14}
                  />
                  <input
                    type="number"
                    min="1"
                    value={rechargeCoins}
                    onChange={(e) => setRechargeCoins(e.target.value)}
                    placeholder="Enter number of coins"
                    className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl text-sm font-bold focus:border-indigo-600 focus:ring-4 focus:ring-indigo-600/5 outline-none transition-all"
                  />
                </div>
                <div className="flex items-center justify-between text-[11px] text-gray-500 mt-1 px-1">
                  <span>Payable Amount:</span>
                  <span className="font-bold text-indigo-600">
                    ₹{rechargeCoins ? Number(rechargeCoins).toLocaleString() : "0"}
                  </span>
                </div>
              </div>

              {rechargeMessage.text && (
                <div
                  className={`p-3 rounded-xl text-xs font-bold flex items-center gap-2 ${
                    rechargeMessage.type === "success"
                      ? "bg-emerald-50 text-emerald-600 border border-emerald-200"
                      : "bg-rose-50 text-rose-600 border border-rose-200"
                  }`}
                >
                  {rechargeMessage.type === "success" ? <FiCheckCircle size={14} /> : <FiX size={14} />}
                  <span>{rechargeMessage.text}</span>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowRechargeModal(false);
                    setRechargeMessage({ text: "", type: "" });
                  }}
                  className="flex-1 py-3 border border-gray-200 rounded-xl text-xs font-bold uppercase tracking-wider text-gray-600 hover:bg-gray-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleRecharge()}
                  disabled={rechargeLoading || !rechargeCoins || Number(rechargeCoins) <= 0}
                  className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold uppercase tracking-wider transition-all shadow-lg shadow-indigo-600/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {rechargeLoading ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <FiZap size={13} />
                      Pay ₹{rechargeCoins ? Number(rechargeCoins).toLocaleString() : "0"}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyWallet;